import React from 'react';
import { Box, Autocomplete, TextField, Typography, Chip } from '@mui/material';
import { Science as ExperimentIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../themes/typography';
import KPIChipsBar from './KPIChipsBar';
import MachineStrip from './MachineStrip';

const CommandCenterTopBar = ({
  filters,
  filterOptions,
  availableLocations,
  onFilterChange,
  machinesData,
  selectedMachineId,
  onSelectMachine,
  selectedMachine,
  loading,
  experimentMode = false,
  onExperimentModeChange,
  period,
  onPeriodChange,
  onMachineSearch
}) => {
  const { t } = useTranslation();

  // Build flat machine list from hierarchical filterOptions for autocomplete
  const allMachineOptions = React.useMemo(() => {
    if (!filterOptions?.groups) return [];
    const machines = [];
    for (const group of filterOptions.groups) {
      for (const cluster of group.clusters || []) {
        for (const location of cluster.locations || []) {
          for (const machine of location.machines || []) {
            machines.push({
              id: machine.id,
              label: machine.alias || String(machine.id),
              group: group.id,
              location: location.id
            });
          }
        }
      }
    }
    return machines.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [filterOptions]);

  return (
    <Box sx={{
      position: 'sticky',
      top: 0,
      zIndex: 10,
      backgroundColor: colors.background.surface,
      borderBottom: `1px solid ${colors.border.light}`,
      px: 2,
      py: 1
    }}>
      {/* Row 1: Filters + Machine Strip + View Selector */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        flexDirection: { xs: 'column', sm: 'row' },
        rowGap: 1
      }}>
        {/* Left: Filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 1, flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 180, maxWidth: 220 }}
            value={filterOptions.groups.find(g => g.id === filters.group) || null}
            onChange={(_, newValue) => onFilterChange('group', newValue?.id || '')}
            options={filterOptions.groups}
            getOptionLabel={(option) => option?.name ? option.name.charAt(0).toUpperCase() + option.name.slice(1) : ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('group')}
                placeholder={t('selectGroup')}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Typography sx={{ fontSize: '0.85rem' }}>
                  {option.name.charAt(0).toUpperCase() + option.name.slice(1)}
                </Typography>
              </li>
            )}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 180, maxWidth: 220 }}
            value={availableLocations.find(l => l.id === filters.location) || null}
            onChange={(_, newValue) => onFilterChange('location', newValue?.id || '')}
            options={availableLocations}
            getOptionLabel={(option) => option?.name ? option.name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            disabled={!filters.group}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('location')}
                placeholder={t('selectLocation')}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Typography sx={{ fontSize: '0.85rem' }}>
                  {option.name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </Typography>
              </li>
            )}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 150, maxWidth: 180 }}
            options={allMachineOptions}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            value={allMachineOptions.find(m => String(m.id) === String(selectedMachineId)) || null}
            onChange={(_, newValue) => {
              if (newValue) {
                onMachineSearch?.(newValue.label, newValue);
              } else {
                // Cleared (X clicked) — reset group, location and machines
                onFilterChange('group', '');
              }
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              return options.filter(o => o.label.includes(inputValue));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('machines_searchMachine')}
                placeholder={t('machines_searchMachine')}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Typography sx={{ fontSize: '0.85rem' }}>{option.label}</Typography>
              </li>
            )}
          />
        </Box>

        {/* Center: Machine Strip (scrollable chips) */}
        {!loading && machinesData.length > 0 && (
          <Box sx={{
            flex: 1,
            minWidth: 0,
            width: { xs: '100%', sm: 'auto' },
            overflow: 'hidden',
            mx: 0.5,
            borderLeft: `1px solid ${colors.border.light}`,
            borderRight: `1px solid ${colors.border.light}`,
            px: 1
          }}>
            <MachineStrip
              machines={machinesData}
              selectedMachineId={selectedMachineId}
              onSelectMachine={onSelectMachine}
            />
          </Box>
        )}

        {/* Right: experiment mode */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {onExperimentModeChange && (
            <Chip
              icon={<ExperimentIcon sx={{ fontSize: 14 }} />}
              label={t('machines_experimentMode')}
              size="small"
              variant={experimentMode ? 'filled' : 'outlined'}
              color={experimentMode ? 'primary' : 'default'}
              onClick={onExperimentModeChange}
              sx={{
                fontSize: '11px',
                height: 28,
                cursor: 'pointer',
                fontWeight: experimentMode ? 600 : 400,
                '& .MuiChip-icon': { color: experimentMode ? 'white' : colors.text.secondary }
              }}
            />
          )}
        </Box>
      </Box>

      {/* Row 2: KPI Chips + Period Pill */}
      {selectedMachine && !loading && (
        <Box sx={{ mt: 0.75 }}>
          <KPIChipsBar machineData={selectedMachine} period={period} onPeriodChange={onPeriodChange} />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(CommandCenterTopBar);
